'use client';
import { Avatar } from '@/components/ui';
import { useNavigation } from '@/contexts/NavigationContext';
import { useProfile } from '@/queries/useProfileQueries';
import { getApiBaseUrl } from '@/utils/apiBaseUrl';
import { useAchievementRecentReceivers } from '../hooks/useAchievementRecentReceivers';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { pluralize } from '@/i18n/pluralize';
import translations from '../AchievementsScreen.translations';

function isToday(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function AchievementRecentReceivers({
  achievementKey,
  myUnlockedAt = null,
}: {
  achievementKey: string;
  iconFile: string;
  /** Current user's unlock time for this achievement, if they earned it. */
  myUnlockedAt?: string | null;
}) {
  const { t, lang } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const { data: profile } = useProfile();
  const { data } = useAchievementRecentReceivers(achievementKey);
  if (!data) return null;

  if (data.recentUnlocks.length === 0 && !myUnlockedAt) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-black p-4 text-center text-xs text-label-tertiary">
        {t('noOneUnlocked')}
      </div>
    );
  }

  const myId = profile?.id;

  // Pin the current user to the top when they've earned the achievement —
  // injecting a synthetic entry if they're not in the recent-unlocks window.
  const others = data.recentUnlocks.filter((r) => r.userId !== myId);
  const mine = myId ? data.recentUnlocks.find((r) => r.userId === myId) : undefined;
  const orderedUnlocks =
    mine
      ? [mine, ...others]
      : myId && myUnlockedAt
        ? [{ userId: myId, firstName: profile?.firstName ?? null, unlockedAt: myUnlockedAt }, ...others]
        : data.recentUnlocks;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-black">
      <ul className="relative flex flex-col p-2">
        {orderedUnlocks.map((r) => {
          const name = r.firstName || '';
          const isMe = !!myId && r.userId === myId;
          const today = isToday(r.unlockedAt);
          return (
            <li key={r.userId}>
              <button
                type="button"
                onClick={() => navigation.navigate('profile', { userId: r.userId })}
                className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-start transition active:opacity-70 ${
                  isMe ? 'bg-white/[0.06] ring-1 ring-amber-400/60' : ''
                }`}
              >
                <Avatar
                  size="s"
                  src={`${getApiBaseUrl()}/api/users/avatar/${r.userId}`}
                  firstName={r.firstName}
                  fallbackKey={r.userId}
                />
                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="min-w-0 truncate text-sm font-medium text-label-primary">{name}</span>
                  {isMe && (
                    <span className="shrink-0 rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">
                      {t('you')}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-label-tertiary">
                  {today ? t('today') : new Date(r.unlockedAt).toLocaleDateString('ru-RU')}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="relative border-t border-white/[0.06] px-4 py-2.5 text-center text-xs text-label-secondary">
        {t('receivedBy')} <span className="font-semibold text-label-primary">{data.totalReceivers}</span>{' '}
        {pluralize(
          data.totalReceivers,
          { one: t('playerOne'), few: t('playerFew'), many: t('playerMany') },
          lang,
        )}
      </div>
    </div>
  );
}
